/**
 * Gym-local wall clock.
 *
 * Competition and session times are stored as `timestamp` with no zone: nine in
 * the morning means nine at the gym, whatever zone anyone reads it from. That
 * makes every one of these a formatting problem, never a conversion problem —
 * converting is precisely the bug.
 *
 * Shared because the Worker decides whether a competition has started, the gym
 * app renders the same windows, and the member app shows the start time. Three
 * copies of this reasoning is three chances to get it wrong differently.
 */

/** Wall-clock datetime, "YYYY-MM-DDTHH:MM" with optional seconds. */
export const WALL_CLOCK_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/

/**
 * A wall-clock string padded to second precision, so two values written at
 * different precisions still compare correctly as strings.
 *
 * The gym form sends "2026-09-30T15:00" (what `datetime-local` gives) while
 * Postgres returns "2026-09-30T15:00:00" for the very same moment. Compared
 * raw, the longer string sorts AFTER the shorter one, so a round ending exactly
 * when its competition ends looked like it fell outside the window and the save
 * was refused. Always compare wall clocks through this.
 */
export function wallClockKey(value: string): string {
  const [date, time = ''] = value.split('T')
  const [h = '00', m = '00', sec = '00'] = time.split(':')
  return `${date}T${h.padStart(2, '0')}:${m.padStart(2, '0')}:${sec.slice(0, 2).padStart(2, '0')}`
}

/**
 * "Now" in a gym's own timezone, as "YYYY-MM-DDTHH:MM:SS", so it compares
 * directly against the stored wall-clock columns.
 *
 * A null timezone falls back to the caller's own zone, which is far closer to a
 * gym's wall clock than UTC would be.
 */
export function gymLocalNow(timezone: string | null | undefined, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone ?? undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(now)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00'
  // Some runtimes report midnight as hour 24; normalise so string compares hold.
  const hour = get('hour') === '24' ? '00' : get('hour')
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}`
}

/**
 * A Date whose *local* components match the stored value.
 *
 * Built from explicit parts on purpose: parsing through `new Date(value)` and
 * formatting in the viewer's zone would move a Bucharest competition by three
 * hours for someone reading from London.
 */
export function parseWallClock(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
  if (!m) return null
  const [, y, mo, d, h, min] = m
  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(min))
}

/** "Thu 20 August, 09:00" in the reader's language, at the gym's wall clock. */
export function formatWallClock(value: string, locale: string): string {
  const date = parseWallClock(value)
  if (!date) return value
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

/** Just the time, for a session row. */
export function formatWallClockTime(value: string): string {
  return value.slice(11, 16)
}

export type CompetitionState = 'upcoming' | 'in_progress' | 'ended'

/**
 * Where a competition sits in its life cycle. Derived rather than stored, so
 * nothing has to be flipped on a schedule. Both sides are wall clock, so this
 * is a string comparison.
 */
export function competitionState(
  competition: { starts_at: string; ends_at: string },
  localNow: string,
): CompetitionState {
  if (localNow < competition.starts_at) return 'upcoming'
  if (localNow > competition.ends_at) return 'ended'
  return 'in_progress'
}
